import { ChevronDown, Globe } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { AVAILABLE_LANGUAGES } from "@/lib/constants/languages";
import { useTranslation } from "react-i18next";
import { useSearchParams, useRouter, usePathname } from "next/navigation";
import { useCallback, useEffect, useMemo } from "react";

const languageOptions = AVAILABLE_LANGUAGES.map((lang) => ({
  code: lang.code,
  label: lang.name,
  short: lang.code.toUpperCase(),
}));
const fallbackLanguages = ["en"];
const normalizeLang = (value: string | null | undefined) =>
  (value || "").split("-")[0].toLowerCase();

interface LanguageSelectorProps {
  availableLanguages?: string[];
  variant?: "full" | "compact";
  className?: string;
}

export function LanguageSelector({
  availableLanguages,
  variant = "full",
  className = "",
}: LanguageSelectorProps) {
  const { i18n } = useTranslation();
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();

  const sortPreferredLanguages = (langs: string[]) =>
    [...langs].sort((a, b) => (a === "en" ? 1 : 0) - (b === "en" ? 1 : 0));

  const allowedLanguageCodes = useMemo(() => {
    const normalized = (availableLanguages?.length
      ? availableLanguages
      : fallbackLanguages
    )
      .map(normalizeLang)
      .filter(Boolean);
    const unique = Array.from(new Set(normalized));
    const supported = unique.filter((code) =>
      languageOptions.some((lang) => lang.code === code),
    );
    const ordered =
      supported.length > 0 ? sortPreferredLanguages(supported) : ["en"];
    return ordered;
  }, [availableLanguages]);

  // Get current language from i18n or URL params
  const currentLang =
    normalizeLang(
      searchParams.get("lang") || i18n.resolvedLanguage || i18n.language,
    ) || "en";
  const activeLang = allowedLanguageCodes.includes(currentLang)
    ? currentLang
    : allowedLanguageCodes[0];
  const selectedLang =
    languageOptions.find((lang) => lang.code === activeLang) ||
    languageOptions[0];
  const triggerTextClass = variant === "compact" ? "text-xs" : "text-sm";
  const visibleLanguageOptions = languageOptions.filter((lang) =>
    allowedLanguageCodes.includes(lang.code),
  );

  const handleLanguageChange = useCallback(
    (newLang: string) => {

      // Update i18n language
      i18n.changeLanguage(newLang);

      // Update URL parameters
      const params = new URLSearchParams(searchParams.toString());
      params.set("lang", newLang);

      const newUrl = `${pathname}?${params.toString()}`;

      // Navigate to the same page with updated language parameter
      router.push(newUrl);

      // For customer portal pages, refresh to get new server data
      if (pathname.includes('/customerportal/')) {
        router.refresh();
      }
    },
    [i18n, router, pathname, searchParams],
  );

  // Don't force language changes - let user's browser language persist
  // Only change language when user explicitly selects from dropdown

  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label="Select language"
          className={`inline-flex items-center gap-1 w-auto h-8 text-xs border-0 bg-transparent hover:bg-accent/50 focus-visible:outline-none focus-visible:ring-0 focus-visible:ring-offset-0 ${className}`}
        >
          <Globe className="h-3.5 w-3.5 text-muted-foreground" />
          <span className={`font-medium ${triggerTextClass}`}>
            {selectedLang?.short}
          </span>
          <span className="sr-only">{selectedLang?.label}</span>
          <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-[120px]">
        <DropdownMenuRadioGroup
          value={activeLang}
          onValueChange={handleLanguageChange}
        >
          {visibleLanguageOptions.map((lang) => (
            <DropdownMenuRadioItem
              key={lang.code}
              value={lang.code}
              className="text-sm"
              aria-label={lang.label}
            >
              <span className="font-medium">{lang.short}</span>
              <span className="sr-only">{lang.label}</span>
            </DropdownMenuRadioItem>
          ))}
        </DropdownMenuRadioGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
